import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useForm, SubmitHandler } from 'react-hook-form';

import useTranslation from 'customHooks/translate';
import { ChangePasswordCustomError, ChangePasswordInputs } from 'types';

import { updatePassword } from '../../services/auth';
import { Input } from './styled';

const Password = () => {
  const navigate = useNavigate();
  const translation = useTranslation();
  const { register, handleSubmit, watch, formState } =
    useForm<ChangePasswordInputs>({
      shouldFocusError: false,
    });

  const [currentPasswordError, setCurrentPasswordError] = useState<
    string | null
  >(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<
    string | null
  >(null);

  const { errors } = formState;

  const pwd = watch('password');

  const { mutate: onPasswordUpdate, isLoading } = useMutation(updatePassword, {
    onSuccess() {
      message.success(translation.passwordChanged);
      navigate('/login', { replace: true });
    },
    onError(err: ChangePasswordCustomError) {
      const errorResponse = err.response?.data?.errors || {};

      setCurrentPasswordError(errorResponse.CurrentPassword?.[0] || null);
      setPasswordError(errorResponse.Password?.[0] || null);
      setConfirmPasswordError(errorResponse.ConfirmPassword?.[0] || null);

      message.error('There was an error updating the password');
    },
  });

  const onSubmit: SubmitHandler<ChangePasswordInputs> = (body) => {
    if (!body.currentPassword || !body.password || !body.confirmPassword) {
      message.error(translation.login.emptyFields);
      return;
    }

    onPasswordUpdate(body);
  };

  return (
    <>
      <div className="md:w-[80%] xl:w-[50%]">
        <div className="mb-[24px] flex flex-col gap-[12px]">
          <p className="text-[20px] font-medium text-[#1D1D1D] md:text-[24px]">
            {translation.password.TabHeader}
          </p>
          <p className="text-[14px] font-normal text-[#1D1D1D]">
            {translation.password.ProfileHeaderSubText}
          </p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-[32px]">
            <div className="flex flex-col gap-[10px]">
              <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                {translation.password.CurrentPassword}
              </label>
              <Input
                id="currentPassword"
                placeholder="• • • • • • • •"
                type="password"
                autoComplete="current-password"
                {...register('currentPassword', {
                  required: translation.passwordRequired,
                })}
                style={{
                  borderColor: errors.currentPassword ? 'red' : '',
                }}
              />
              {errors.currentPassword && (
                <div className="text-[12px] text-red-600">
                  {errors.currentPassword.message}
                </div>
              )}
              {currentPasswordError && (
                <span
                  className="animate__animated animate__shakeX"
                  style={{ color: 'red', fontSize: '12px' }}
                >
                  {currentPasswordError}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-[10px]">
              <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                {translation.password.NewPassword}
              </label>
              <Input
                id="password"
                placeholder="• • • • • • • •"
                type="password"
                autoComplete="new-password"
                {...register('password', {
                  required: translation.passwordRequired,
                })}
                style={{
                  borderColor: errors.password ? 'red' : '',
                }}
              />
              {errors.password && (
                <div className="text-[12px] text-red-600">
                  {errors.password.message}
                </div>
              )}
              {passwordError && (
                <span
                  className="animate__animated animate__shakeX"
                  style={{ color: 'red', fontSize: '12px' }}
                >
                  {passwordError}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-[10px]">
              <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                {translation.password.PasswordConfirm}
              </label>
              <Input
                id="confirmPassword"
                placeholder="• • • • • • • •"
                type="password"
                autoComplete="new-password"
                {...register('confirmPassword', {
                  validate: (value) =>
                    value === pwd || translation.passwordNotMatch,
                })}
                style={{
                  borderColor: errors.confirmPassword ? 'red' : '',
                }}
              />

              {errors.confirmPassword && (
                <div className="text-[12px] text-red-600">
                  {errors.confirmPassword.message}
                </div>
              )}
              {confirmPasswordError && (
                <span
                  className="animate__animated animate__shakeX"
                  style={{ color: 'red', fontSize: '12px' }}
                >
                  {confirmPasswordError}
                </span>
              )}
            </div>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[10px]">
              <button className="w-full rounded-[6px] border border-[rgba(41,45,50,0.25)] bg-white px-[24px] py-[12px] text-[14px] font-normal leading-[16px] text-[#292d32] md:text-[16px]">
                <Link className="hover:text-inherit" to={'/dashboard'}>
                  {translation.info.cancelButton}
                </Link>
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex w-full items-center justify-center gap-2 rounded-[6px] bg-[#466afb] px-[24px] py-[12px] text-[14px] font-normal leading-[16px] text-white disabled:cursor-not-allowed disabled:opacity-70 md:text-[16px]"
              >
                {isLoading && (
                  <span
                    className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                    aria-hidden="true"
                  />
                )}
                <span>
                  {isLoading
                    ? translation.messages.loading
                    : translation.info.saveButton}
                </span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default Password;
